import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './VerHistorias.css';

const formatCount = (num) => {
  const n = Number(num) || 0;
  if (n >= 1000000) {
    return Math.round(n / 1000000) + ' M';
  }
  return n.toLocaleString('es-CL');
};

const VistasBadge = ({ count }) => (
  <div className="badge-wrap vh-vistas-badge">
    <div className="badge badge-static">
      <div className="icon-row">
        <i className="fas fa-eye eye-icon"></i>
        <span className="num">{formatCount(count)}</span>
      </div>
    </div>
  </div>
);

const getViewerToken = () => {
  let token = localStorage.getItem('viewer_token');
  if (!token) {
    token = 'vt_' + Date.now() + '_' + Math.random().toString(36).substring(2, 12);
    localStorage.setItem('viewer_token', token);
  }
  return token;
};

const VerHistorias = ({ usuarioId, nombrePerfil, fotoPerfil, esCreador = false, onClose, onSubirHistoria }) => {
  const [historias, setHistorias] = useState([]);
  const [indiceActual, setIndiceActual] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progreso, setProgreso] = useState(0);
  const [eliminando, setEliminando] = useState(false);
  const [silenciado, setSilenciado] = useState(false);
  const [mostrarConfirmarEliminar, setMostrarConfirmarEliminar] = useState(false);
  const videoElRef = useRef(null);

  const API_CONTENIDO = process.env.REACT_APP_HISTORIAS_CONTENIDO_URL;

  const timerRef = useRef(null);
  const progresoRef = useRef(null);
  const DURACION_FOTO = 5000;

  const API_BASE = process.env.REACT_APP_HISTORIAS_VIEWER_URL;
  const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.body.dataset.scrollY = scrollY;
    return () => {
      const scrollYGuardado = parseInt(document.body.dataset.scrollY || '0');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      delete document.body.dataset.scrollY;
      window.scrollTo(0, scrollYGuardado);
    };
  }, []);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await axios.get(`${API_BASE}/get_historias.php?usuario_id=${usuarioId}`);
        if (res.data.success) {
          setHistorias(res.data.data || []);
        }
      } catch (err) {
        console.error('Error al cargar historias:', err);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [usuarioId, API_BASE]);

  const tiempoTranscurrido = (created_at) => {
    const creado = new Date(created_at);
    const ahora = new Date();
    const diffMs = ahora - creado;
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffHoras >= 1) return `${diffHoras}h`;
    if (diffMins >= 1) return `${diffMins}m`;
    return 'ahora';
  };

const eliminarHistoria = async () => {
    setMostrarConfirmarEliminar(false);
    setEliminando(true);
    try {
      const res = await axios.post(`${API_CONTENIDO}/eliminar_historia.php`, {
        historia_id: historias[indiceActual].id,
        usuario_id: usuarioId
      });
      if (res.data.success) {
        const nuevas = historias.filter((_, i) => i !== indiceActual);
        if (nuevas.length === 0) {
          onClose();
          return;
        }
        setHistorias(nuevas);
        setIndiceActual(prev => Math.min(prev, nuevas.length - 1));
      }
    } catch (err) {
      console.error('Error al eliminar historia:', err);
    } finally {
      setEliminando(false);
    }
  };

const siguiente = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(progresoRef.current);
    setProgreso(0);
    setSilenciado(false);
    if (indiceActual < historias.length - 1) {
      setIndiceActual(prev => prev + 1);
    } else {
      onClose();
    }
  }, [indiceActual, historias.length, onClose]);

  useEffect(() => {
    if (!historias.length || loading) return;
    setProgreso(0);
    clearTimeout(timerRef.current);
    clearInterval(progresoRef.current);

    const historia = historias[indiceActual];
    if (historia?.tipo === 'foto') {
      const intervalo = 50;
      const pasos = DURACION_FOTO / intervalo;
      let paso = 0;
      progresoRef.current = setInterval(() => {
        paso++;
        setProgreso((paso / pasos) * 100);
        if (paso >= pasos) clearInterval(progresoRef.current);
      }, intervalo);
      timerRef.current = setTimeout(siguiente, DURACION_FOTO);
    }
    // Para video: la barra se actualiza via onTimeUpdate en el elemento video
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progresoRef.current);
    };
  }, [indiceActual, historias, loading, siguiente]);

  useEffect(() => {
    if (!historias.length || loading || esCreador) return;
    const historia = historias[indiceActual];
    if (!historia) return;
    axios.post(`${API_BASE}/registrar_vista_historia.php`, {
      historia_id: historia.id,
      token: getViewerToken()
    }).catch(err => console.error('Error al registrar vista de historia:', err));
  }, [indiceActual, historias, loading, esCreador, API_BASE]);

  if (loading) {
    return (
      <div className="vh-overlay">
        <div className="vh-loading">
          <div className="vh-spinner"></div>
          <p>Cargando historias...</p>
        </div>
      </div>
    );
  }

  if (!historias.length) {
    return (
      <div className="vh-overlay" onClick={(e) => e.target.classList.contains('vh-overlay') && onClose()}>
        <div className="vh-sin-historias">
          <p>No hay historias disponibles</p>
          {esCreador && (
            <button className="vh-btn-subir" onClick={onSubirHistoria}>
              + Subir historia
            </button>
          )}
          <button className="vh-cerrar-btn" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    );
  }

  const historiaActual = historias[indiceActual];
  const urlArchivo = `${UPLOADS_URL}/ID_${usuarioId}/historias/${historiaActual.archivo}?v=${historiaActual.id}`;

  return (
    <div className="vh-overlay">
      {/* Barras de progreso */}
      <div className="vh-barras">
        {historias.map((_, i) => (
          <div key={i} className="vh-barra-bg">
            <div
              className="vh-barra-fill"
              style={{
                width: i < indiceActual ? '100%' : i === indiceActual ? `${progreso}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="vh-header">
        <div className="vh-info">
          {fotoPerfil ? (
  <img src={fotoPerfil} alt={nombrePerfil} className="vh-avatar vh-avatar-img" draggable={false} onContextMenu={(e) => e.preventDefault()} />
) : (
  <div className="vh-avatar"></div>
)}
          <span className="vh-nombre">{nombrePerfil}</span>
          <span className="vh-tiempo">{tiempoTranscurrido(historiaActual.created_at)}</span>
        </div>
<div className="vh-header-btns">
          {esCreador && (
            <>
              <button 
                className="vh-btn-eliminar" 
                onClick={() => setMostrarConfirmarEliminar(true)}
                disabled={eliminando}
                title="Eliminar historia"
              >
                {eliminando ? '...' : 'Eliminar'}
              </button>
              <button className="vh-btn-mas" onClick={onSubirHistoria} title="Subir historia">+</button>
            </>
          )}
          {historias[indiceActual]?.tipo === 'video' && (
            <button
              className="vh-btn-sonido"
              onClick={() => {
                const nuevoSilencio = !silenciado;
                setSilenciado(nuevoSilencio);
                if (videoElRef.current) videoElRef.current.muted = nuevoSilencio;
              }}
              title={silenciado ? 'Activar sonido' : 'Silenciar'}
            >
              {silenciado ? '🔇' : '🔊'}
            </button>
          )}
          <button className="vh-close-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Contenido � click avanza */}
      <div className="vh-contenido" onClick={siguiente}>
        {historiaActual.tipo === 'foto' ? (
          <img src={urlArchivo} alt="Historia" className="vh-media" draggable={false} onContextMenu={(e) => e.preventDefault()} />
        ) : (
<video
            key={urlArchivo}
            ref={videoElRef}
            src={urlArchivo}
            autoPlay
            muted={silenciado}
            playsInline
              disablePictureInPicture
              disableRemotePlayback
              onContextMenu={(e) => e.preventDefault()}
            className="vh-media"
            onEnded={siguiente}
            onTimeUpdate={(e) => {
              const video = e.target;
              if (video.duration && video.duration > 0) {
                setProgreso((video.currentTime / video.duration) * 100);
              }
            }}
          />
        )}
      </div>

      <VistasBadge count={historiaActual.vistas_count} />

      {mostrarConfirmarEliminar && (
        <div className="vh-confirm-overlay">
          <div className="vh-confirm-box">
            <p>¿Estás seguro que quieres eliminar esta historia?</p>
            <div className="vh-confirm-btns">
              <button className="vh-confirm-btn-cancelar" onClick={() => setMostrarConfirmarEliminar(false)}>Cancelar</button>
              <button className="vh-confirm-btn-aceptar" onClick={eliminarHistoria}>Aceptar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerHistorias;